import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Input, Button, Checkbox, Steps, Select, message } from "antd";
import axios from "axios";

const { Step } = Steps;
const { Option } = Select;

function AddBankAccount({ onClose }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const handleNext = async () => {
    try {
      await form.validateFields();
      setCurrentStep(currentStep + 1);
    } catch (error) {
      message.error("Please fill in all required fields.");
    }
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue(true);
      const { consent, ...rest } = values;

      const payload = {
        ...rest,
        gateway: "66a4a702a34d22fa0073b587", // Add gateway ID
      };
      try {
        await axios.post(
          `${import.meta.env.VITE_API_URL_DASHBOARD_URL}/account`,
          payload
        );
      } catch (error) {
        console.log("error while adding account ", error);
      }

      message.success("Bank account added successfully!");
      onClose(); // Close the modal
      navigate("/dashboard");
    } catch (error) {
      console.error("Error while submitting the form:", error);
      message.error("Failed to add bank account. Please try again.");
    }
  };

  const stepContent = [
    // Step 1: Basic Information
    <>
      <Form.Item
        label="Phone"
        name="phone"
        rules={[{ required: true, message: "Phone is required." }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Legal Business Name"
        name="legal_business_name"
        rules={[
          { required: true, message: "Legal Business Name is required." },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Business Type"
        name="business_type"
        rules={[{ required: true, message: "Business Type is required." }]}
      >
        <Select placeholder="Select a business type">
          <Option value="private_limited">Private Limited</Option>
          <Option value="proprietorship">Proprietorship</Option>
          <Option value="partnership">Partnership</Option>
          <Option value="individual">Individual</Option>
          <Option value="public_limited">Public Limited</Option>
          <Option value="llp">LLP</Option>
          <Option value="trust">Trust</Option>
          <Option value="society">Society</Option>
          <Option value="ngo">NGO</Option>
        </Select>
      </Form.Item>
      <Form.Item
        label="Contact Name"
        name="contact_name"
        rules={[{ required: true, message: "Contact Name is required." }]}
      >
        <Input />
      </Form.Item>
    </>,
    // Step 2: Bank Details
    <>
      <Form.Item
        label="Account Holder Name"
        name={["bank_account", "account_holder_name"]}
        rules={[
          { required: true, message: "Account Holder Name is required." },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Account Number"
        name={["bank_account", "account_number"]}
        rules={[{ required: true, message: "Account Number is required." }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Confirm Account Number"
        name={["bank_account", "confirm_account_number"]}
        dependencies={[["bank_account", "account_number"]]}
        rules={[
          { required: true, message: "Confirm Account Number is required." },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (
                !value ||
                getFieldValue(["bank_account", "account_number"]) === value
              ) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error("The account numbers do not match!")
              );
            },
          }),
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="IFSC Code"
        name={["bank_account", "ifsc_code"]}
        rules={[
          { required: true, message: "IFSC Code is required." },
          {
            pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/,
            message: "Invalid IFSC Code format.",
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Bank Name"
        name={["bank_account", "bank_name"]}
        rules={[{ required: true, message: "Bank Name is required." }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Branch"
        name={["bank_account", "branch"]}
        rules={[{ required: true, message: "Branch is required." }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Account Type"
        name={["bank_account", "account_type"]}
        rules={[{ required: true, message: "Account Type is required." }]}
      >
        <Select >
          <Input  value="savings">Savings</Input>
        </Select>
      </Form.Item>
    </>,
    // Step 3: Confirmation
    <>
      <Form.Item
        name="consent"
        valuePropName="checked"
        rules={[
          {
            validator: (_, value) =>
              value
                ? Promise.resolve()
                : Promise.reject("You must agree to the terms."),
          },
        ]}
      >
        <Checkbox>I agree to the terms and conditions.</Checkbox>
      </Form.Item>
    </>,
  ];

  return (
    <div className="flex justify-center items-center bg-white min-h-80">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8">
        <Steps current={currentStep} className="mb-8">
          <Step title="Step 1" description="Basic Information" />
          <Step title="Step 2" description="Bank Details" />
          <Step title="Step 3" description="Confirmation" />
        </Steps>
        <Form form={form} layout="vertical">
          {stepContent[currentStep]}
        </Form>
        <div className="flex justify-between mt-6">
          {currentStep > 0 && (
            <Button
              onClick={() => setCurrentStep(currentStep - 1)}
              className="bg-[#2d98b3] text-white"
            >
              Back
            </Button>
          )}
          {currentStep < 2 && (
            <Button
              type="primary"
              onClick={handleNext}
              className="bg-[#2d98b3] text-white"
            >
              Next
            </Button>
          )}
          {currentStep === 2 && (
            <Button
              type="primary"
              onClick={handleSubmit}
              className="bg-[#2d98b3] text-white"
            >
              Submit
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AddBankAccount;
